import React, { useState, useEffect } from 'react';
import PageTemplate from '../components/Common/PageTemplate';
import { motion } from 'framer-motion';
import { FaUniversalAccess, FaGraduationCap, FaBuilding, FaUsers, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const EngelsizYasam = () => {
  const { t } = useTranslation();
  const pillarsData = t('accessible_life.pillars', { returnObjects: true });
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const iconMap = {
    0: <FaBuilding className="w-10 h-10" />,
    1: <FaGraduationCap className="w-10 h-10" />,
    2: <FaUsers className="w-10 h-10" />,
    3: <FaUniversalAccess className="w-10 h-10" />
  };

  const pillars = Array.isArray(pillarsData) ? pillarsData.map((pillar, index) => ({
    icon: iconMap[index],
    title: pillar.title,
    description: pillar.description
  })) : [];

  const galleryImages = [
    { id: 2, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/30134f6b-44a9-4291-baaf-49e43b23e86c.jpg', alt: t('accessible_life.gallery.title') + ' 2' },
    { id: 3, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/3303816e-7b38-4358-861d-8b966bbcea54.jpg', alt: t('accessible_life.gallery.title') + ' 3' },
    { id: 4, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/4b030901-b72f-410a-9d5f-ccedaa4c24aa.jpg', alt: t('accessible_life.gallery.title') + ' 4' },
    { id: 5, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/4c7df7cb-02cb-4bc0-bcaf-f429f49cb58a.jpg', alt: t('accessible_life.gallery.title') + ' 5' },
    { id: 6, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/6e9abe95-a0a5-4a5a-935f-1c289dcdbf53.jpg', alt: t('accessible_life.gallery.title') + ' 6' },
    { id: 7, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/756fe869-d6b9-4513-a861-8a1404596078.jpg', alt: t('accessible_life.gallery.title') + ' 7' },
    { id: 8, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/8bbf2a07-6cc5-49e7-a7f1-df33a467f5b5.jpg', alt: t('accessible_life.gallery.title') + ' 8' },
    { id: 9, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/a7edb9a6-916b-4033-93db-72db3462661f.jpg', alt: t('accessible_life.gallery.title') + ' 9' },
    { id: 10, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/c6b25971-bcba-41fe-89a0-a855d2603814.jpg', alt: t('accessible_life.gallery.title') + ' 10' },
    { id: 11, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/c8ab19cb-cfac-49a3-8d09-393afe4bd0a1.jpg', alt: t('accessible_life.gallery.title') + ' 11' },
    { id: 12, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/dacf0fcc-6afb-4504-9a4c-204844a60faa.jpg', alt: t('accessible_life.gallery.title') + ' 12' },
    { id: 13, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/e8127a3b-088b-419d-ac48-3617b628833e.jpg', alt: t('accessible_life.gallery.title') + ' 13' },
    { id: 14, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/e866766a-15d1-4e44-9db1-4c26d39f6c2b.jpg', alt: t('accessible_life.gallery.title') + ' 14' },
    { id: 15, src: 'https://fs.hacettepe.edu.tr/fenyeni/engelsizyasam/ead8edf0-129e-47c6-9887-f7107e356178.jpg', alt: t('accessible_life.gallery.title') + ' 15' }
  ];

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  };

  // Tüm resimleri önceden yükle
  useEffect(() => {
    galleryImages.forEach((img) => {
      const image = new Image();
      image.src = img.src;
    });
  }, []);

  // Modal açıldığında bir sonraki ve bir önceki resmi preload et
  useEffect(() => {
    if (selectedImageIndex !== null) {
      const nextIndex = selectedImageIndex === galleryImages.length - 1 ? 0 : selectedImageIndex + 1;
      const prevIndex = selectedImageIndex === 0 ? galleryImages.length - 1 : selectedImageIndex - 1;

      const nextImg = new Image();
      nextImg.src = galleryImages[nextIndex].src;

      const prevImg = new Image();
      prevImg.src = galleryImages[prevIndex].src;
    }
  }, [selectedImageIndex, galleryImages]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'Escape') setSelectedImageIndex(null);
      if (e.key === 'ArrowLeft') handlePrevImage();
      if (e.key === 'ArrowRight') handleNextImage();
    };

    window.addEventListener('keydown', handleKeyPress);

    // Modal açıkken scroll'u engelle
    if (selectedImageIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImageIndex, handlePrevImage, handleNextImage]);

  return (
    <PageTemplate title={t('accessible_life.title')} subtitle={t('accessible_life.subtitle')}>
      <div className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

          {/* Giriş Vizyon Metni */}
          <motion.div
            className="text-center p-10 rounded-3xl bg-gradient-to-r from-blue-50 via-white to-sky-50 shadow-lg mb-24"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
              {t('accessible_life.vision.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('accessible_life.vision.description')}
            </p>
          </motion.div>

          {/* Temel İlkeler */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-400 ease-in-out transform hover:-translate-y-2 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.15, ease: "easeOut" }}
              >
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-20 w-20 rounded-full bg-blue-600 text-white">
                    {pillar.icon}
                  </div>
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-bold text-gray-900">{pillar.title}</h3>
                  <p className="mt-2 text-gray-500">{pillar.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <div
                key={img.id}
                onClick={() => setSelectedImageIndex(index)}
                className="aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer bg-gray-200"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading={index < 8 ? "eager" : "lazy"}
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>

          {/* Lightbox Modal */}
          {selectedImageIndex !== null && (
            <div
              className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
              onClick={() => setSelectedImageIndex(null)}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedImageIndex(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
                aria-label="Close"
              >
                <FaTimes className="w-8 h-8" />
              </button>

              {/* Previous Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevImage();
                }}
                className="absolute left-4 text-white hover:text-gray-300 transition-colors z-50"
                aria-label="Previous"
              >
                <FaChevronLeft className="w-8 h-8" />
              </button>

              {/* Image */}
              <div
                className="max-w-7xl max-h-[90vh] p-4"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={galleryImages[selectedImageIndex].src}
                  alt={galleryImages[selectedImageIndex].alt}
                  loading="eager"
                  decoding="async"
                  className="max-w-full max-h-[90vh] object-contain"
                />
              </div>

              {/* Next Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
                className="absolute right-4 text-white hover:text-gray-300 transition-colors z-50"
                aria-label="Next"
              >
                <FaChevronRight className="w-8 h-8" />
              </button>

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
                {selectedImageIndex + 1} / {galleryImages.length}
              </div>
            </div>
          )}
        </div>
      </div>
    </PageTemplate>
  );
};

export default EngelsizYasam;
